import React from 'react'
import { J1LeftarrowD } from '@e3dc-react/icons'
import { Box, Card, CardMedia, IconButton, Typography } from '@mui/material'
import { Swiper, SwiperSlide } from 'swiper/react'
import MoreInfoProductCategories from '../MoreInfoProductCategories'
import { makeStyles } from 'tss-react/mui'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import NoImage from 'src/Resources/Images/shop/NoImage.png'
import { Pagination, Navigation } from 'swiper'
import { Product, getImageUrlBySize } from 'src/Libs/shop'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { QuantityAvailabilityIndicator } from '../../ShopProduct'

interface TransformerMoreInfoProductProps {
  productData: Product
  onMoreInfoProductChange: (productId: null) => void
}

const useStyles = makeStyles()(theme => ({
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '--swiper-theme-color': theme.palette.grey.grey09,
    '.swiper-pagination-bullet-active': {
      transform: 'scale(1.5)',
    },
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },

  exitIcon: {
    color: theme.palette.grey.grey04,
    width: 36,
    height: 36,
    transform: 'translateX(-30px) translateY(-30px)',
    zIndex: 1,
    position: 'absolute',
  },
}))

const TransformerMoreInfoProduct: React.FC<TransformerMoreInfoProductProps> = ({ productData, onMoreInfoProductChange }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const unitPrice = productData?.calculatedPrice?.unitPrice ?? 0
  const price = productData?.calculatedPrice?.listPrice?.price ?? 0
  const productAvailability = productData?.extensions?.e3dcAvailability
  const productImages = productData?.media
  return (
    <Box>
      <IconButton className={classes.exitIcon}>
        <J1LeftarrowD
          onClick={() => {
            onMoreInfoProductChange(null)
          }}
        />
      </IconButton>
      <Box>
        {productData.manufacturer?.translated && (
          <Typography variant="h5" color="primary">
            {productData.manufacturer.translated.name}
          </Typography>
        )}
        {productData?.translated && (
          <Typography variant="h1">
            {productData.translated.name} <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">{t('shop.product.number') + productData.productNumber}</Typography>
          <Box sx={{ display: 'flex' }}>
            <Typography variant="h6" color={unitPrice < price ? 'primary' : 'inherit'}>
              {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
            </Typography>
            {unitPrice < price && (
              <Typography variant="h6" sx={{ textDecorationLine: 'line-through', marginLeft: '32px' }}>
                {currencyFormatter(price)}
              </Typography>
            )}
          </Box>
        </Box>
        <Swiper
          pagination={{
            clickable: true,
          }}
          loop
          navigation={true}
          spaceBetween={30}
          modules={[Navigation, Pagination]}
          className={classes.imageContainer}
        >
          {productData.media !== null ? (
            <>
              {productImages
                ?.sort((a, b) => a.position - b.position)
                .map(image => {
                  const productImageUrl = getImageUrlBySize(image.media.thumbnails, ShopImageThumnbailSizes.Medium)
                  return (
                    <SwiperSlide key={image.position} className={classes.image}>
                      <Card sx={{ my: 1, padding: 0 }}>
                        <CardMedia className={classes.image} component="img" height="194" image={productImageUrl} alt="System konfigurieren" />
                      </Card>
                    </SwiperSlide>
                  )
                })}
            </>
          ) : (
            <SwiperSlide className={classes.image}>
              <Card sx={{ minHeight: '390px', my: 1, padding: 0 }}>
                <CardMedia className={classes.image} component="img" height="194" image={NoImage} alt="System konfigurieren" />
              </Card>
            </SwiperSlide>
          )}
        </Swiper>
      </Box>
      {productData && (
        <MoreInfoProductCategories
          productDescription={productData.translated?.description}
          width={productData.width}
          height={productData.height}
          length={productData.length}
          weight={productData.weight}
          properties={productData.properties}
          documents={productData.extensions.downloads.downloads}
        />
      )}
    </Box>
  )
}

export default TransformerMoreInfoProduct
